<template>
    <div class="input-container" :class="id">
        <div v-if="title" class="title body__medium">{{ title }}</div>
        <div class="input-box">
            <div class="box">
                <div v-if="hasSlot" class="slot-wrapper">
                    <slot name="icon"/>
                </div>
                <input
                    v-if="mask"
                    :value="value"
                    :type="getInputType"
                    :placeholder="placeholder"
                    :class="{ 'no-valid': error, filled: value && id === 'otp' }"
                    class="body__large"
                    :disabled="disabled"
                    v-mask="mask"
                    @input="updateInputValue"
                    :ref="inputRef"
                />
                <input
                    v-else
                    :value="value"
                    :type="getInputType"
                    :placeholder="placeholder"
                    :class="{ 'no-valid': error }"
                    class="body__large"
                    :disabled="disabled"
                    @input="updateInputValue"
                />
                <div v-if="showIcons" class="icons">
                    <BaseToggleSmallButton
                        v-if="value && type !== 'password'"
                        @click.native="clearInputValue">
                            <template #icon>
                                <i class="material-symbols-outlined">close</i>
                            </template>
                    </BaseToggleSmallButton>
                    <BaseToggleSmallButton
                        v-if="type === 'password'"
                        @click.native="showValue">
                            <template #icon>
                                <i class="material-symbols-outlined">
                                    {{ visibilityState ? 'visibility' : 'visibility_off' }}
                                </i>
                            </template>
                    </BaseToggleSmallButton>
                </div>
            </div>
            <div v-if="$slots['info']" class="info">
                <slot name="info"/>
            </div>
        </div>
        <small v-if="info">{{ info }}</small>
    </div>
</template>

<script>
import BaseToggleSmallButton from "@/new-design/shared/ui/buttons/BaseToggleSmallButton.vue"

export default {
    name: "BaseInput",
    components: {BaseToggleSmallButton},
    props: {
        value: {
            type: [String, Number],
            default: ""
        },
        placeholder: {
            type: String,
            default: ""
        },
        title: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'text'
        },
        error: {
            type: Boolean,
            default: false
        },
        info: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        mask: {
            type: String,
            default: ''
        },
        showIcons: {
            type: Boolean,
            default: true
        },
        id: {
            type: String,
            default: ''
        },
        inputRef:{
            type: String,
            default: ''
        },
        validateFunction:{
            type: Function
        }
    },
    data() {
        return {
            visibilityState: false
        }
    },
    computed: {
        hasSlot() {
            return !!this.$slots.icon
        },
        getInputType() {
            return this.type === "password" && this.visibilityState
                ? "text"
                : this.type
        }
    },
    methods: {
        showValue() {
            this.visibilityState = !this.visibilityState
        },
        updateInputValue(e) {
            let newValue = e.target.value
            if (this.type === "number") {
                newValue = this.validateNumberInput(newValue)
            }

            if(this.validateFunction) {
                newValue = this.validateFunction(newValue);
                e.target.value = newValue;
            }

            this.$emit("input", newValue)
        },
        validateNumberInput(value) {
            return value
                .replace(/[^0-9.,]/g, "")
                .replace(/(\..*)\./g, "$1")
                .replace(/(,.*),/g, "$1")
                .replace(/(,.*)\./g, "$1")
                .replace(/(\..*),/g, "$1")
        },
        clearInputValue() {
            this.$emit("input", "")
        }
    }
}
</script>

<style scoped>
.input-box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    border-radius: 4px;
    border: 1px solid var(--System-Outline);
    transition: all .3s ease;
}

.input-box:has(input.filled),
.input-box:has(input:focus) {
    border-color: var(--System-Primary);
}

.title {
    display: flex;
    padding-bottom: 4px;
}

.box {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
}

input {
    padding: 10px 40px 10px 8px;
    outline: none;
    height: 100%;
    background: transparent;
    color: var(--System-OnSurface);
    width: 100%;
    transition: all .3s ease;
    border-radius: 4px;
    border: none
}

input::placeholder {
    font-size: 16px;
    font-weight: 400;
    color: var(--System-Outline);
}

.slot-wrapper {
    margin: auto 5px 7px 10px;
    max-height: 25px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.icons {
    cursor: pointer;
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    gap: 8px;
    display: flex;
    align-items: center;
}

.icons i {
    color: var(--System-OnSurfaceVariant);
}

.toggle-small-button {
    display: flex;
    align-items: center;
    justify-content: center;
}

.no-valid {
    border-color: var(--System-Error);
}

.info {
    display: flex;
    cursor: default;
    padding-left: 8px;
    padding-right: 8px;
    border-left: 1px solid var(--System-OutlineVariant);
}

.otp input {
    padding: 8px;
    text-align: center;
    width: 40px;
    height: 40px;
}

@media (max-width: 767px) {
    .input-container.info-slot-hidden-mobile .info{
        display: none;
    }
}
</style>
